/**
 * "Ours · Tharstern · Δ" — the single comparison primitive behind the
 * product owner's UX rule for the cost-engine validation pages: every
 * measured quantity (a money value, a time, or a quantity) shows our
 * engine's figure, Tharstern's recorded figure, and the signed difference
 * together, in that order — never one alone, never the difference alone.
 *
 * Two variants, both driven by the same pure `computeComparisonTriplet`:
 * - **Table** — `ComparisonGroupHeaderCells` + `ComparisonSubHeaderCells`
 *   for a two-row grouped header, `ComparisonCells` for the three
 *   `<TableCell>`s in each body row.
 * - **Inline** — `ComparisonInline`, a single banner-text span, for
 *   difference banners, drill-in summaries, and tooltips.
 *
 * Colour lives only on Δ, reusing the same green/amber/red tokens as the
 * rest of the validation surfaces (`ValidationTone` in
 * `cost-engine-validation-presentation.ts`) — exact is green, a small
 * difference is amber, anything larger is red.
 */
import {
  computeComparisonTriplet,
  type ComparisonTone,
  type ComparisonValue,
} from "@shared-core/domain/engine-tharstern-comparison";
import { TableCell, TableHead } from "../primitives/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "../primitives/tooltip";
import { cn } from "../primitives/utils";

const DELTA_TONE_CLASSES: Record<ComparisonTone, string> = {
  success: "text-[color:var(--color-success)]",
  warning: "text-amber-700 dark:text-amber-400 font-semibold",
  error: "text-destructive font-semibold",
  neutral: "text-muted-foreground",
};

/** Top header row: one cell spanning all three columns, naming the group
 *  ("Time", "Value", "Money"…). Pair with `ComparisonSubHeaderCells` on the
 *  row beneath. */
export function ComparisonGroupHeaderCell({
  label,
  className,
  colSpan = 3,
}: {
  label: string;
  className?: string;
  /** Defaults to 3 (Ours/Tharstern/Δ). A grouped state-column header (e.g.
   *  "Cost lines" over Matches/Small differences/Mismatch) passes its own
   *  column count — same two-row grouped-header pattern, different span. */
  colSpan?: number;
}) {
  return (
    <TableHead
      colSpan={colSpan}
      className={cn(
        "border-l border-border text-center text-xs uppercase tracking-wide",
        className,
      )}
    >
      {label}
    </TableHead>
  );
}

/** Second header row: the three column labels under a group header. */
export function ComparisonSubHeaderCells() {
  return (
    <>
      <TableHead className="border-l border-border text-right text-xs font-normal text-muted-foreground">
        Ours
      </TableHead>
      <TableHead className="text-right text-xs font-normal text-muted-foreground">
        Tharstern
      </TableHead>
      <TableHead className="text-right text-xs font-normal text-muted-foreground">
        Δ
      </TableHead>
    </>
  );
}

/** The three body cells for one row's measurement — spread inside a
 *  `<TableRow>` alongside `ComparisonSubHeaderCells`'s column order. */
export function ComparisonCells({
  value,
  className,
  deltaClassName,
}: {
  value: ComparisonValue;
  className?: string;
  /** Extra classes for the Δ cell only, on top of `className` — use to make
   *  a page's primary comparison visually dominant (e.g. the cost-pair
   *  page's Value column next to its secondary Time column). */
  deltaClassName?: string;
}) {
  const t = computeComparisonTriplet(value);
  const deltaCell = (
    <TableCell
      className={cn(
        "text-right tabular-nums",
        DELTA_TONE_CLASSES[t.tone],
        className,
        deltaClassName,
      )}
    >
      {t.deltaText}
      {t.deltaPercentText && (
        <span className="ml-1 font-normal opacity-80">
          ({t.deltaPercentText})
        </span>
      )}
    </TableCell>
  );
  return (
    <>
      <TableCell
        className={cn(
          "border-l border-border text-right tabular-nums",
          className,
        )}
      >
        {t.oursText}
      </TableCell>
      <TableCell className={cn("text-right tabular-nums", className)}>
        {t.hasTharstern || !value.dashReason ? (
          t.tharsternText
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help underline decoration-dotted underline-offset-2">
                {t.tharsternText}
              </span>
            </TooltipTrigger>
            <TooltipContent>{value.dashReason}</TooltipContent>
          </Tooltip>
        )}
      </TableCell>
      {deltaCell}
    </>
  );
}

/** Banner-text rendering — "Ours: X · Tharstern: Y · Δ Z (P%)" — for
 *  difference banners, drill-in summaries, and cell tooltips. */
export function ComparisonInline({
  value,
  label,
  className,
  inheritColor = false,
}: {
  value: ComparisonValue;
  label?: string;
  className?: string;
  /** Inside a tooltip (solid primary background) the tone colours are
   *  unreadable; inherit the container's text colour and rely on weight. */
  inheritColor?: boolean;
}) {
  const t = computeComparisonTriplet(value);
  return (
    <span
      className={cn(
        "inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5",
        className,
      )}
    >
      {label && <span className="font-medium">{label}</span>}
      <span>Ours {t.oursText}</span>
      <span>·</span>
      {!t.hasTharstern && value.dashReason ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help underline decoration-dotted underline-offset-2">
              Tharstern {t.tharsternText}
            </span>
          </TooltipTrigger>
          <TooltipContent>{value.dashReason}</TooltipContent>
        </Tooltip>
      ) : (
        <span>Tharstern {t.tharsternText}</span>
      )}
      <span>·</span>
      <span
        className={inheritColor ? "font-semibold" : DELTA_TONE_CLASSES[t.tone]}
      >
        Δ {t.deltaText}
        {t.deltaPercentText ? ` (${t.deltaPercentText})` : ""}
      </span>
    </span>
  );
}
